import React, { useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import { FaCalendarCheck, FaSpinner } from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { fetchFirstSuccessful, formatShortDate, normalizeStatus, toArray } from '../utils/formExperienceApi';
import '../styles/ManageCurriculums.css';
import '../styles/ManageRanks.css';
import 'react-calendar/dist/Calendar.css';

// Real backend: GET /attendance/admin-overview?curriculum_id=&student_id=&rotation_number=
// (see PharmD_System/routers/attendance.py get_attendance_admin_overview). There is no
// dedicated filter-options endpoint — curricula come from GET /curriculum/curriculums and
// students come from GET /curriculum/{curriculum_id}/students once a curriculum is chosen.
const STUDENT_ATTENDANCE_ENDPOINTS = ['/attendance/admin-overview'];

const mapSubmissionToDayEntries = (submission) => {
    const days = toArray(submission?.days);
    const status = normalizeStatus(submission?.status || 'pending');
    const rotation = submission?.rotation_number != null ? Number(submission.rotation_number) + 1 : '—';
    const curriculum = submission?.curriculum_id || '—';

    if (!days.length) {
        return [{
            id: submission?.id || `attendance-${Math.random()}`,
            date: submission?.submitted_at,
            status,
            rotation,
            curriculum,
            notes: submission?.note || ''
        }];
    }

    return days.map((day, index) => ({
        id: `${submission?.id || 'submission'}-${index}`,
        date: day?.date,
        timeSlot: day?.start_time && day?.end_time ? `${day.start_time} - ${day.end_time}` : '',
        status,
        rotation,
        curriculum,
        notes: submission?.note || ''
    }));
};

const tileClass = (status) => {
    if (status.includes('approved')) return 'mr-cal-approved';
    if (status.includes('reject')) return 'mr-cal-rejected';
    return 'mr-cal-pending';
};

const pillClass = (status) => {
    if (status.includes('approved')) return 'is-status-active';
    if (status.includes('reject')) return '';
    return 'is-status-upcoming';
};

const StudentAttendancePage = ({ adminMode = false }) => {
    const [options, setOptions] = useState({ curricula: [], students: [] });
    const [selectedCurriculum, setSelectedCurriculum] = useState('');
    const [selectedStudent, setSelectedStudent] = useState('');
    const [selectedRotation, setSelectedRotation] = useState('');
    const [entries, setEntries] = useState([]);
    const [summary, setSummary] = useState({ total: 0, approved: 0, pending: 0, rejected: 0 });
    const [loading, setLoading] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [error, setError] = useState('');
    const [hasLoaded, setHasLoaded] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());

    useEffect(() => {
        const loadCurricula = async () => {
            try {
                setLoadingOptions(true);
                const res = await axiosInstance.get('/curriculum/curriculums');
                setOptions((prev) => ({ ...prev, curricula: Array.isArray(res.data) ? res.data : [] }));
            } catch (_err) {
                setOptions((prev) => ({ ...prev, curricula: [] }));
            } finally {
                setLoadingOptions(false);
            }
        };
        loadCurricula();
    }, []);

    useEffect(() => {
        setSelectedStudent('');
        if (!selectedCurriculum) {
            setOptions((prev) => ({ ...prev, students: [] }));
            return;
        }
        const loadStudents = async () => {
            try {
                setLoadingStudents(true);
                const res = await axiosInstance.get(`/curriculum/${selectedCurriculum}/students`);
                const students = Array.isArray(res.data?.students) ? res.data.students : [];
                setOptions((prev) => ({ ...prev, students }));
            } catch (_err) {
                setOptions((prev) => ({ ...prev, students: [] }));
            } finally {
                setLoadingStudents(false);
            }
        };
        loadStudents();
    }, [selectedCurriculum]);

    const loadAttendance = async () => {
        if (!selectedCurriculum || !selectedStudent) {
            setError('Curriculum and student filters are required.');
            return;
        }

        setError('');
        try {
            setLoading(true);
            const params = {
                curriculum_id: selectedCurriculum,
                student_id: selectedStudent
            };
            if (selectedRotation) params.rotation_number = Number(selectedRotation) - 1;
            const payload = await fetchFirstSuccessful(STUDENT_ATTENDANCE_ENDPOINTS, { params });
            const data = payload?.data || payload || {};
            const submissions = toArray(data?.submissions || data?.items || data);
            const mapped = submissions.flatMap(mapSubmissionToDayEntries);
            setEntries(mapped);
            setHasLoaded(true);

            setSummary({
                total: data?.summary?.unique_submissions ?? submissions.length,
                approved: data?.summary?.approved_count ?? mapped.filter((item) => item.status.includes('approved')).length,
                pending: data?.summary?.pending_count ?? mapped.filter((item) => item.status.includes('pending') || item.status.includes('submitted')).length,
                rejected: data?.summary?.rejected_count ?? mapped.filter((item) => item.status.includes('reject')).length
            });
        } catch (err) {
            setError(err?.response?.data?.message || 'Unable to load student attendance entries.');
        } finally {
            setLoading(false);
        }
    };

    const entriesByDate = useMemo(() => {
        const map = new Map();
        entries.forEach((item) => {
            if (!item.date) return;
            const key = new Date(item.date).toDateString();
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(item);
        });
        return map;
    }, [entries]);

    const selectedEntries = entriesByDate.get(selectedDate.toDateString()) || [];

    const tileClassName = ({ date, view }) => {
        if (view !== 'month') return '';
        const values = entriesByDate.get(date.toDateString()) || [];
        if (!values.length) return '';
        if (values.some((item) => item.status.includes('approved'))) return tileClass('approved');
        if (values.some((item) => item.status.includes('reject'))) return tileClass('rejected');
        return tileClass('pending');
    };

    return (
        <div className="mc-page">
            <div className="universal-white-container mc-shell">
                <div className="mc-hero">
                    <div className="mc-hero__copy">
                        <span className="mc-eyebrow">{adminMode ? 'Administration' : 'Preceptor'}</span>
                        <h1>Student Attendance</h1>
                        <p>Choose a curriculum and student to review their submitted attendance in detail.</p>
                    </div>
                    {hasLoaded && (
                        <div className="mc-hero__stats">
                            <div className="mc-stat"><strong>{summary.total}</strong><span>Submissions</span></div>
                            <div className="mc-stat"><strong>{summary.approved}</strong><span>Approved</span></div>
                            <div className="mc-stat"><strong>{summary.pending}</strong><span>Pending</span></div>
                            <div className="mc-stat"><strong>{summary.rejected}</strong><span>Rejected</span></div>
                        </div>
                    )}
                </div>

                <div className="mc-toolbar">
                    <select
                        className="mr-status-filter"
                        value={selectedCurriculum}
                        onChange={(e) => setSelectedCurriculum(e.target.value)}
                        disabled={loadingOptions}
                    >
                        <option value="">Select curriculum *</option>
                        {options.curricula.map((curriculum, index) => (
                            <option key={curriculum?._id || index} value={curriculum?._id}>
                                {curriculum?.name || curriculum?._id}
                            </option>
                        ))}
                    </select>
                    <select
                        className="mr-status-filter"
                        value={selectedStudent}
                        onChange={(e) => setSelectedStudent(e.target.value)}
                        disabled={!selectedCurriculum || loadingStudents}
                    >
                        <option value="">{loadingStudents ? 'Loading students…' : 'Select student *'}</option>
                        {options.students.map((student, index) => (
                            <option key={student?.studentId || student?._id || index} value={student?.studentId || student?._id}>
                                {`${student?.firstname || ''} ${student?.lastname || ''}`.trim() || student?.studentId}
                            </option>
                        ))}
                    </select>
                    <input
                        type="number"
                        min="1"
                        placeholder="Rotation # (optional)"
                        value={selectedRotation}
                        onChange={(e) => setSelectedRotation(e.target.value)}
                        className="mr-status-filter"
                        style={{ minWidth: '160px' }}
                    />
                    <button type="button" className="mc-primary-btn" onClick={loadAttendance} disabled={loading}>
                        {loading ? <FaSpinner className="mc-spin" /> : 'Load Attendance'}
                    </button>
                </div>

                {error && (
                    <div className="mc-empty-state">
                        <FaCalendarCheck />
                        <h3>Unable to load attendance</h3>
                        <p>{error}</p>
                    </div>
                )}

                {!error && loading && (
                    <div style={{ display: 'grid', placeItems: 'center', minHeight: '30vh' }}>
                        <FaSpinner className="mc-spin" style={{ fontSize: '2.5rem', color: '#4a90e2', animation: 'spin 1s linear infinite' }} />
                    </div>
                )}

                {!loading && !error && hasLoaded && entries.length > 0 && (
                    <div className="mr-attendance-layout">
                        <div className="mc-form-card mr-calendar-panel">
                            <Calendar value={selectedDate} onChange={(value) => setSelectedDate(value)} tileClassName={tileClassName} />
                        </div>
                        <div className="mc-form-card mr-day-panel">
                            <h4>{selectedDate.toLocaleDateString()}</h4>
                            {selectedEntries.length === 0 && <p className="mr-hint">No entries on this day.</p>}
                            {selectedEntries.map((entry) => (
                                <div key={entry.id} className="mr-day-entry">
                                    <div>
                                        <strong>Rotation {entry.rotation}</strong>
                                        {entry.timeSlot && <p>{entry.timeSlot}</p>}
                                        <small>{entry.notes || formatShortDate(entry.date)}</small>
                                    </div>
                                    <span className={`mc-status-pill ${pillClass(entry.status)}`}>{entry.status}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!loading && !error && hasLoaded && entries.length === 0 && (
                    <div className="mc-empty-state">
                        <FaCalendarCheck />
                        <h3>No attendance records</h3>
                        <p>This student has no attendance submissions matching the selected filters</p>
                    </div>
                )}

                {!loading && !error && !hasLoaded && (
                    <div className="mc-empty-state">
                        <FaCalendarCheck />
                        <h3>No attendance loaded yet</h3>
                        <p>Select required filters above and click "Load Attendance"</p>
                    </div>
                )}

                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        </div>
    );
};

export default StudentAttendancePage;
